/**
 * FilterSubSection — one searchable checkbox list inside a report filter drawer
 * (header with Select All / Clear / Show Selected, a search box, and the list).
 *
 * subDef shape:
 *   key          — working-state key passed back to onToggle/onSelectAll/onClear
 *   label        — header text; also drives the search placeholder
 *   idKey        — option field used as the (stringified) checkbox id
 *   nameKey      — option field shown as the item name
 *   subtextKey   — optional; option field shown as a second line and included in search
 *   subtextLabel — optional; prefix for the second line (e.g. 'Serial')
 *   subtext2Key / subtext2Label — optional; a further line, shown and searched the same way
 *                 (the Devices filter's location within the area)
 *   descriptionKey — optional; option field shown in an ⓘ popover after the name, on
 *                 rows that have one (DescriptionTip: event type descriptions). Not searched.
 *
 * Styling comes from the ar-filter-* rules in
 * pages/reports/alert-details/alert-details-page.css, which the host page must load.
 */

import { useState, useMemo } from 'react';
import DescriptionTip from './DescriptionTip.jsx';

const FilterSubSection = ({ subDef, options, selected, onToggle, onSelectAll, onClear }) => {
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    // Second-line fields, in display order. Each is shown when present and searched.
    const subtexts = useMemo(() => [
        subDef.subtextKey ? { key: subDef.subtextKey, label: subDef.subtextLabel } : null,
        subDef.subtext2Key ? { key: subDef.subtext2Key, label: subDef.subtext2Label } : null,
    ].filter(Boolean), [subDef.subtextKey, subDef.subtextLabel, subDef.subtext2Key, subDef.subtext2Label]);
    const hasSubtext = subtexts.length > 0;

    const filteredOptions = useMemo(() => {
        let list = options || [];
        if (showSelectedOnly) {
            list = list.filter((opt) => selected.has(String(opt[subDef.idKey])));
        }
        if (search) {
            const term = search.toLowerCase();
            list = list.filter((opt) =>
                (opt[subDef.nameKey] || '').toLowerCase().includes(term) ||
                subtexts.some((st) => String(opt[st.key] || '').toLowerCase().includes(term))
            );
        }
        return list;
    }, [options, search, subDef.idKey, subDef.nameKey, subtexts, selected, showSelectedOnly]);

    return (
        <div className="ar-filter-section">
            <div className="ar-filter-section__header">
                <span className="ar-filter-section__label">{subDef.label}</span>
                <div className="ar-filter-section__actions">
                    <button
                        className="ar-filter-section__btn"
                        onClick={() => onSelectAll(subDef.key, (options || []).map((o) => String(o[subDef.idKey])))}
                        type="button"
                    >
                        Select All
                    </button>
                    <button
                        className="ar-filter-section__btn"
                        onClick={() => onClear(subDef.key)}
                        type="button"
                    >
                        Clear
                    </button>
                    <button
                        className={`ar-filter-section__btn${showSelectedOnly ? ' ar-filter-section__btn--active' : ''}`}
                        onClick={() => setShowSelectedOnly((prev) => !prev)}
                        type="button"
                    >
                        Show Selected
                    </button>
                </div>
            </div>
            <input
                className="ar-filter-section__search"
                type="text"
                placeholder={`Search ${subDef.label.toLowerCase()}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            <div className="ar-filter-list">
                {filteredOptions.length === 0 ? (
                    <p className="ar-filter-list__empty">
                        {showSelectedOnly ? 'No selections.' : 'No options found.'}
                    </p>
                ) : null}
                {filteredOptions.map((opt) => {
                    const id = String(opt[subDef.idKey]);
                    const checked = selected.has(id);
                    const name = opt[subDef.nameKey] || '(empty)';
                    const description = subDef.descriptionKey ? opt[subDef.descriptionKey] : null;
                    return (
                        <label key={id} className={`ar-filter-list__item${hasSubtext ? ' ar-filter-list__item--has-subtext' : ''}`}>
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => onToggle(subDef.key, id)}
                            />
                            {hasSubtext ? (
                                <span className="ar-filter-list__item-content">
                                    <span className="ar-filter-list__item-name">{name}<DescriptionTip name={name} description={description} /></span>
                                    {subtexts.map((st) => (opt[st.key] ? (
                                        <span key={st.key} className="ar-filter-list__item-subtext">
                                            {st.label ? `${st.label}: ` : ''}{opt[st.key]}
                                        </span>
                                    ) : null))}
                                </span>
                            ) : (
                                <span className="ar-filter-list__item-name">{name}<DescriptionTip name={name} description={description} /></span>
                            )}
                        </label>
                    );
                })}
            </div>
        </div>
    );
};

export default FilterSubSection;
